/**
 * ProtectedRoute
 * Componente que protege rutas verificando autenticación y permisos.
 *
 * HIPAA Requirement: 45 CFR 164.308(a)(4) - Information Access Management
 *
 * ============================================================================
 * DOCUMENTACIÓN PARA REFACTORIZACIÓN CON BACKEND
 * ============================================================================
 *
 * ESTADO ACTUAL:
 * - Verifica autenticación (user existe)
 * - Verifica permisos usando PermissionsContext (frontend)
 * - Redirige a /Login si no autenticado
 * - Redirige a /Unauthorized si no tiene permisos
 *
 * CAMBIOS REQUERIDOS EN BACKEND:
 * 1. Validar permisos en cada endpoint de API (no confiar solo en frontend)
 * 2. Retornar 403 Forbidden si el usuario no tiene permisos
 * 3. Implementar middleware de autorización en el backend
 *
 * ============================================================================
 */

import React, { useContext } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext/AuthContext";
import { usePermissions, ROUTE_PERMISSIONS } from "../../context/PermissionsContext";

/**
 * ProtectedRoute - Protege rutas por autenticación y permisos
 *
 * @param {string} requiredPermission - Permiso específico requerido (opcional)
 * @param {string[]} requiredPermissions - Lista de permisos requeridos (opcional)
 * @param {boolean} requireAll - Si true, requiere TODOS los permisos; si false, requiere AL MENOS UNO
 */
const ProtectedRoute = ({
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}) => {
  const { user } = useContext(AuthContext);
  const { hasPermission, hasAnyPermission, hasAllPermissions, canAccessRoute } = usePermissions();
  const location = useLocation();

  // 1. Verificar autenticación
  if (!user) {
    return <Navigate to="/Login" state={{ from: location }} replace />;
  }

  // 2. Verificar permiso específico si se proporciona
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/Unauthorized" state={{ from: location }} replace />;
  }

  // 3. Verificar lista de permisos si se proporciona
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return <Navigate to="/Unauthorized" state={{ from: location }} replace />;
    }
  }

  // 4. Verificar permisos por ruta (si está definido en ROUTE_PERMISSIONS)
  const routePermission = ROUTE_PERMISSIONS[location.pathname];
  if (routePermission && !hasPermission(routePermission)) {
    return <Navigate to="/Unauthorized" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
